import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";

export default function QRScannerModal({ open, onClose, onResult }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const streamRef = useRef(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setErr("");

    const start = async () => {
      try {
        const video = videoRef.current;
        if (!video) return;

        // 1) Ask for camera stream explicitly (rear camera preferred)
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });

        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = stream;

        // 2) Attach to video element
        video.srcObject = stream;
        video.setAttribute("playsinline", "true"); // iOS
        await video.play();

        // 3) Start ZXing decoder
        const reader = new BrowserMultiFormatReader();
        readerRef.current = reader;

        reader.decodeFromVideoElement(video, (result, error) => {
          if (cancelled) return;
          if (result) {
            onResult(result.getText());
            onClose();
          }
        });
      } catch (e) {
        console.error(e);
        const msg =
          e?.name === "NotAllowedError"
            ? "Camera permission denied. Please allow camera access in browser settings."
            : e?.name === "NotFoundError"
            ? "No camera found on this device."
            : "Camera could not start. Try closing other apps using the camera and retry.";
        setErr(msg);
      }
    };

    start();

    return () => {
      cancelled = true;

      try {
        readerRef.current?.reset?.();
      } catch {}

      try {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
      } catch {}
    };
  }, [open, onClose, onResult]);

  if (!open) return null;

  return (
    <div style={styles.backdrop}>
      <div style={styles.modal}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>Scan QR</h3>
          <button onClick={onClose}>Close</button>
        </div>

        {err && <p style={{ color: "red", marginTop: 10 }}>{err}</p>}

        <video
          ref={videoRef}
          muted
          style={{ width: "100%", borderRadius: 8, marginTop: 10, background: "#000" }}
        />

        <p style={{ fontSize: 12, opacity: 0.8, marginTop: 8 }}>
          Tip: use good lighting and hold steady.
        </p>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 9999,
  },
  modal: {
    width: "min(520px, 95vw)",
    background: "white",
    borderRadius: 12,
    padding: 14,
  },
};
